import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import i18n, {
  Ti18nKeys,
  messageWithArrayExtraData,
  messageWithExtraData
} from "../../../constants/i18n";

import { colors } from "../../../core/et3-theme/colors";
import {
  minAge,
  maxAge,
  maxHeight,
  maxWeight,
  minHeight,
  minWeight
} from "../../../constants/user-profile";
import { getUserProfileValue } from "../../../helpers/et3-get-user-edit-profile-data";
import {
  ALL_COUNTRY_PICKER_ENTRIES,
  ALL_MARITAL_STATUS_FEMALE,
  ALL_MARITAL_STATUS_MALE,
  EDUCATION_ENTRIES,
  WORK_ENTRIES
} from "../../../constants/picker-entries-constants";
import { TConfirmationPopUps } from "../../../Types";
import getUserEditProfileStore from "../../user-store/edit-profile-store";
import {
  EDIT_KEYS,
  TEt3PickerSingleValue,
  TEt3PickerOption
} from "../../../TypesNEW";
import { reduce } from "et3-type-guards";
import { getCountryByKey } from "../../../helpers/et3-data-helper";
import { clearDelay, delay } from "../../../helpers/et3-timeout-helper";
import { DELAY_KEYS } from "../../../constants/delay-constants";
import getUserAdvancedStore from "../../user-store/advanced-store";
import getCoreStorageStore from "../core-storage-store";
import {
  E_LOCAL_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../../constants/storage-constants";

class CorePopUpStore {
  isAlertVisible = observable.box<boolean>(false);
  alertHeader = observable.box<Ti18nKeys>("EMPTY_STRING");
  alertMessage = observable.box<Ti18nKeys>("EMPTY_STRING");
  alertAction = observable.box<Ti18nKeys>("EMPTY_STRING");
  MessageExtraData = observable.box<string>("");
  MessageArrayExtraData = observable.box<string[]>([]);
  alertColor = observable.box<string>();
  investigateActionID = observable.box();
  editPopUpVisibleType = observable.box<EDIT_KEYS>("");
  editPopUpValue = observable.box<TEt3PickerSingleValue>({
    value: "",
    label: ""
  });
  isShowUpdatePasswordPopUpVisible = observable.box<boolean>(false);
  isNotificationPermissionPopUpVisible = observable.box<boolean>(false);
  isPicAlbumPopUpVisible = observable.box<boolean>(false);
  confirmationPopUpType = observable.box<TConfirmationPopUps>("EMPTY_STRING");

  setEditPopUpValue = (value: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.editPopUpValue.set(value);
    });
  };

  showAlertMessage = (message: Ti18nKeys) => {
    this.alertColor.set(colors.all.red);
    this.alertHeader.set("OH_NO");
    this.alertMessage.set(message);
    this.isAlertVisible.set(true);
  };

  showAlertMessageWithExtraData = (message: Ti18nKeys, extraData: string) => {
    this.alertColor.set(colors.all.red);
    this.alertHeader.set("OH_NO");
    this.alertMessage.set(message);
    this.MessageExtraData.set(extraData);
    this.isAlertVisible.set(true);
  };

  showWarningMessage = (message: Ti18nKeys) => {
    this.alertColor.set(colors.all.red);
    this.alertHeader.set("WARNING");
    this.alertMessage.set(message);
    this.isAlertVisible.set(true);
  };

  getAlertTone = computed(() => {
    switch (this.alertHeader.get()) {
      case "OH_NO":
      case "WARNING":
      case "EMPTY_STRING":
        return { color: "error.main", Icon: ErrorOutlineIcon };
      case "SUCCESS":
        return { color: "success.main", Icon: CheckCircleOutlineIcon };
      default:
        return { color: "info.main", Icon: InfoOutlinedIcon };
    }
  });

  copyMessageAndClose = () => {
    navigator.clipboard.writeText(this.MessageExtraData.get());
    this.closePopUps();
  };

  getAlertActionButton = computed(
    (): { labelKey: Ti18nKeys; onClick: () => void } => {
      switch (this.alertAction.get()) {
        case "COPY":
          return { labelKey: "COPY", onClick: this.copyMessageAndClose };
        default:
          return { labelKey: "OK", onClick: this.closePopUps };
      }
    }
  );

  closingPopAndAlert = async () => {
    await delay(2000, DELAY_KEYS.POPUP_TIMEOUT);
    this.isPicAlbumPopUpVisible
      ? this.isAlertVisible.set(false)
      : this.closePopUps();
    clearDelay(DELAY_KEYS.POPUP_TIMEOUT);
  };

  showSuccessMessage = async (message: Ti18nKeys) => {
    this.alertColor.set(colors.all.green);
    this.alertHeader.set("SUCCESS");
    this.alertMessage.set(message);
    this.isAlertVisible.set(true);
    this.closingPopAndAlert();
  };

  showSuccessMessageWithExtraData = async (
    message: Ti18nKeys,
    extraData: string,
    isDelayed: boolean
  ) => {
    this.alertColor.set(colors.all.green);
    this.alertHeader.set("SUCCESS");
    this.alertMessage.set(message);
    this.MessageExtraData.set(extraData);
    this.isAlertVisible.set(true);
    if (isDelayed) {
      this.closingPopAndAlert();
    }
  };

  showHelperMessage = (message: Ti18nKeys) => {
    this.alertColor.set(colors.all.gray);
    this.alertHeader.set("HELPER");
    this.alertMessage.set(message);
    this.isAlertVisible.set(true);
  };
  showHelperMessageExtraData = (message: Ti18nKeys, extraData: string[]) => {
    this.alertColor.set(colors.all.gray);
    this.alertHeader.set("HELPER");
    this.alertMessage.set(message);
    this.MessageArrayExtraData.set(extraData);
    this.isAlertVisible.set(true);
  };
  closePopUps = () => {
    runInAction(() => {
      this.alertColor.set("");
      this.alertHeader.set("EMPTY_STRING");
      this.alertAction.set("EMPTY_STRING");
      this.isAlertVisible.set(false);
    });
  };
  closeChangePasswordPopUps = () => {
    this.isShowUpdatePasswordPopUpVisible.set(false);
  };
  openEditCountryPopUp = () => {
    this.editPopUpVisibleType.set("COUNTRY");
  };
  openEditAgePopUp = () => {
    this.editPopUpVisibleType.set("AGE");
  };
  openEditWeightPopUp = () => {
    this.editPopUpVisibleType.set("WEIGHT");
  };
  openEditHeightPopUp = () => {
    this.editPopUpVisibleType.set("HEIGHT");
  };
  openEditMaritalStatusPopUp = () => {
    this.editPopUpVisibleType.set("MARITAL_STATUS");
  };
  openEditWorkPopUp = () => {
    this.editPopUpVisibleType.set("WORK");
  };
  openEditEducationPopUp = () => {
    this.editPopUpVisibleType.set("EDUCATION");
  };
  openUpdatePasswordPopUp = () => {
    this.isShowUpdatePasswordPopUpVisible.set(true);
  };
  openNotificationPermissionPopUp = () => {
    const isAnswered = getCoreStorageStore(E_STORAGE_TYPE.LOCAL).get<boolean>(
      E_LOCAL_STORAGE_KEY.NOTIFICATION_PERMISSION_POPUP_ANSWERED
    );

    if (isAnswered) {
      return;
    }

    this.isNotificationPermissionPopUpVisible.set(true);
  };
  answerNotificationPermissionPopUp = () => {
    getCoreStorageStore(E_STORAGE_TYPE.LOCAL).set(
      E_LOCAL_STORAGE_KEY.NOTIFICATION_PERMISSION_POPUP_ANSWERED,
      true
    );
    this.isNotificationPermissionPopUpVisible.set(false);
  };
  openPicAlbumPopUp = () => {
    this.isPicAlbumPopUpVisible.set(true);
  };

  closePicAlbumPopUp = () => {
    runInAction(() => {
      this.isPicAlbumPopUpVisible.set(false);
    });
  };

  getFinalMessage = () => {
    let finalMessage = "";
    const message: Ti18nKeys = this.alertMessage.get();
    if (messageWithExtraData.includes(message)) {
      finalMessage = i18n.getWithExtraData(
        message,
        this.MessageExtraData.get().toString()
      );
    } else if (messageWithArrayExtraData.includes(message)) {
      finalMessage = i18n.getWithArrayExtraData(
        message,
        this.MessageArrayExtraData.get()
      );
    } else {
      finalMessage = i18n.get(message);
    }
    return finalMessage;
  };

  getEditPopUpData = () => {
    const editPopUpVisibleType = this.editPopUpVisibleType.get();
    let options: TEt3PickerOption[] = [
      {
        label: "",
        value: ""
      }
    ];
    let existingValue: {
      value: string;
      label: string;
    };
    let key: EDIT_KEYS = "";

    switch (editPopUpVisibleType) {
      case "AGE":
        let ageEntries = [];
        for (let i = minAge; i <= maxAge; i++) {
          ageEntries.push({ label: `${i}`, value: `${i}` });
        }
        return {
          options: ageEntries,
          existingValue: {
            value: `${getUserProfileValue("AGE")}`,
            label: `${getUserProfileValue("AGE")}`
          },
          key: "AGE"
        };
      case "COUNTRY":
        return {
          options: ALL_COUNTRY_PICKER_ENTRIES,
          existingValue: {
            value: `${getUserProfileValue("NATIONALITY")}`,
            label: getCountryByKey(`${getUserProfileValue("NATIONALITY")}`)
          },
          key: "NATIONALITY"
        };
      case "EDUCATION":
        return {
          options: EDUCATION_ENTRIES,
          existingValue: {
            value: reduce(
              () =>
                EDUCATION_ENTRIES.filter((value) => {
                  return value.value === `${getUserProfileValue("EDUCATION")}`;
                })[0].label,
              ""
            ),
            label: reduce(
              () =>
                EDUCATION_ENTRIES.filter((value) => {
                  return value.value === `${getUserProfileValue("EDUCATION")}`;
                })[0].label,
              ""
            )
          },
          key: "EDUCATION"
        };
      case "HEIGHT":
        let heightEntries = [];
        for (let i = minHeight; i <= maxHeight; i++) {
          heightEntries.push({ label: `${i}`, value: `${i}` });
        }
        return {
          options: heightEntries,
          existingValue: {
            value: `${getUserProfileValue("HEIGHT")}`,
            label: `${getUserProfileValue("HEIGHT")}`
          },
          key: "HEIGHT"
        };
      case "MARITAL_STATUS":
        return {
          options:
            getUserEditProfileStore().userToEditCurrentGender.get() === "m"
              ? ALL_MARITAL_STATUS_MALE
              : ALL_MARITAL_STATUS_FEMALE,
          existingValue: {
            value: reduce(
              () =>
                ALL_MARITAL_STATUS_MALE.filter((value) => {
                  return value.value === getUserProfileValue("MARITAL_STATUS");
                })[0].value,
              ""
            ),
            label: reduce(
              () =>
                ALL_MARITAL_STATUS_MALE.filter((value) => {
                  return value.value === getUserProfileValue("MARITAL_STATUS");
                })[0].label,
              ""
            )
          },
          key: "MARITAL_STATUS"
        };
      case "WEIGHT":
        let weightEntries = [];
        for (let i = minWeight; i <= maxWeight; i++) {
          weightEntries.push({ label: `${i}`, value: `${i}` });
        }
        return {
          options: weightEntries,
          existingValue: {
            value: `${getUserProfileValue("WEIGHT")}`,
            label: `${getUserProfileValue("WEIGHT")}`
          },
          key: "WEIGHT"
        };
      case "WORK":
        return {
          options: WORK_ENTRIES,
          existingValue: {
            value: reduce(
              () =>
                WORK_ENTRIES.filter((value) => {
                  return value.value === `${getUserProfileValue("WORK")}`;
                })[0].label,
              ""
            ),
            label: reduce(
              () =>
                WORK_ENTRIES.filter((value) => {
                  return value.value === `${getUserProfileValue("WORK")}`;
                })[0].label,
              ""
            )
          },
          key: "WORK"
        };
      default:
        return {
          options: options,
          existingValue: {
            value: existingValue,
            label: existingValue
          },
          key: key
        };
    }
  };

  closeConfirmationPopUps = () => {
    this.confirmationPopUpType.set("EMPTY_STRING");
    getUserAdvancedStore().adminCommentText.set("");
  };

  openConfirmationPopUps = (confirmationPopUpText: TConfirmationPopUps) => {
    this.confirmationPopUpType.set(confirmationPopUpText);
  };
}

const getCorePopUpStore = memoize(
  () => new CorePopUpStore(),
  () => 1
);
export default getCorePopUpStore;
